import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Copy, Eye, EyeOff, Key } from "lucide-react";
import { useCallback, useState } from "react";

export default function APIKeysTable({ apiKeys }: { apiKeys: any[] }) {
    const [showApiKey, setShowApiKey] = useState<{ [key: string]: boolean }>({});
    const { toast } = useToast();

    const copyToClipboard = useCallback(
        (text: string) => {
            navigator.clipboard.writeText(text);
            toast({
                title: "Copied!",
                description: "API key copied to clipboard",
                variant: "default",
            });
        },
        [toast]
    );

    const toggleApiKeyVisibility = useCallback((keyId: string) => {
        setShowApiKey((prev) => ({
            ...prev,
            [keyId]: !prev[keyId],
        }));
    }, []);

    return (
        <div className="space-y-4">
            {/* Table Header - Hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b bg-gray-50 text-sm font-medium text-gray-700">
                <div className="col-span-3">NAME</div>
                <div className="col-span-6">KEY</div>
                <div className="col-span-3">OPTIONS</div>
            </div>

            {/* API Keys List */}
            {apiKeys.map((key) => (
                <div key={key.id}>
                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b hover:bg-gray-50 items-center">
                        {/* NAME */}
                        <div className="col-span-3">
                            <div className="font-medium text-gray-900 truncate">{key.name}</div>
                        </div>

                        {/* KEY */}
                        <div className="col-span-6">
                            <div className="flex items-center space-x-2">
                                <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded flex-1 truncate">
                                    {showApiKey[key.id]
                                        ? (key.unkey || 'No key available')
                                        : key.unkey ? `${key.unkey?.substring(0, 8)}-***************************` : 'No key available'
                                    }
                                </code>
                            </div>
                        </div>

                        {/* OPTIONS */}
                        <div className="col-span-3">
                            <div className="flex items-center space-x-1">
                                {/* Show/Hide Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() =>
                                        setShowApiKey((prev) => ({
                                            ...prev,
                                            [key.id]: !prev[key.id],
                                        }))
                                    }
                                >
                                    {showApiKey[key.id] ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </Button>

                                {/* Copy Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => copyToClipboard(key.unkey)}
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Layout */}
                    <div className="md:hidden p-4 border rounded-lg space-y-3">
                        {/* Header with name and actions */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                                <Key className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <h4 className="font-medium text-gray-900 truncate">{key.name}</h4>
                            </div>
                            <div className="flex items-center space-x-1 flex-shrink-0">
                                {/* Show/Hide Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => toggleApiKeyVisibility(key.id)}
                                >
                                    {showApiKey[key.id] ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </Button>

                                {/* Copy Button */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => copyToClipboard(key.unkey)}
                                >
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* API Key */}
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">API Key</label>
                            <code className="block text-sm font-mono bg-gray-100 px-3 py-2 rounded break-all">
                                {showApiKey[key.id]
                                    ? (key.unkey || 'No key available')
                                    : key.unkey ? `${key.unkey?.substring(0, 8)}${'*'.repeat(Math.max(0, key.unkey.length - 8))}` : 'No key available'
                                }
                            </code>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
